import React, { useEffect, useState, useRef, useCallback } from 'react';
import QueueService from './QueueService';
import MarkdownParser from './MarkdownParser';
import { formatIST } from '../utils/dateUtils';
import './ChatList.css';

const ChatList = ({ onChatClick, selectedChat, phone_number_id }) => {
  const [chats, setChats] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pagination states for infinite scroll
  const [after, setAfter] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'inactive'

  // Search states
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const observerRef = useRef(null);

  // 1. Fetch chats (newest set) initially
  const fetchChats = useCallback(async (cursorAfter = null) => {
    try {
      setLoading(true);
      const data = await QueueService.FetchRecentUsersWithMessages(cursorAfter, phone_number_id, filter, searchQuery);
      /**
       *  Expecting structure:
       *  {
       *    messages: [...],
       *    after: "...",
       *    before: "...",
       *  }
       */
      if (data.messages && data.messages.length > 0) {
        setChats((prev) => [...prev, ...data.messages]);
      } else {
        setHasMore(false);
      }
      // Update the "after" cursor
      if (data.after) {
        setAfter(data.after);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching chats:', err);
      setError('Failed to load chats');
    } finally {
      setLoading(false);
    }
  }, [filter, searchQuery, phone_number_id]);

  useEffect(() => {
    // Reset states when filter or search changes
    setChats([]);
    setAfter(null);
    setHasMore(true);
    // eslint-disable-next-line
  }, [filter, searchQuery]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (val.length >= 4 || val.length === 0) {
      setSearchQuery(val);
    }
  };

  // 2. Infinite scroll: watch sentinel
  useEffect(() => {
    if (loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [target] = entries;
        if (target.isIntersecting) {
          // Load next page
          fetchChats(after);
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [after, hasMore, loading, fetchChats]);

  // 3. Handle chat item click
  const handleChatItemClick = (chatItem) => {
    onChatClick(chatItem);
  };

  // 4. Format timestamp using IST utility
  const formatTimestamp = (timeString) => {
    if (!timeString) return '';
    return formatIST(timeString, 'relative');
  };

  // Remove the frontend filtering logic
  const filteredChats = chats;

  if (error) {
    return <p style={{ margin: '1rem' }}>Error: {error}</p>;
  }

  return (
    <div className="chat-list-container">

      <div className="chat-list-header-modern">
        <div className="search-bar-container">
          <input
            type="number"
            className="modern-search-input"
            placeholder="Search number"
            value={searchText}
            onChange={handleSearchChange}
            style={{ margin: '0px' }}
          />
        </div>

        {/* Desktop Filter Tags */}
        <div className="filter-tags-container">
          <button
            className={`filter-tag ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-tag ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button
            className={`filter-tag ${filter === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilter('inactive')}
          >
            Inactive
          </button>
        </div>

        {/* Mobile Filter Dropdown */}
        <div className="filter-dropdown-mobile">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="modern-mobile-select"
          >
            <option value="all">All Chats</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <ul className="chat-list">
        {filteredChats.map((chat) => (
          <li
            key={chat.user_id}
            className={`chat-list-item ${selectedChat?.user_id === chat.user_id ? 'selected-chat' : ''
              } ${chat.status ? '' : 'chat-inactive'}`}
            onClick={() => handleChatItemClick(chat)}
            style={{
              cursor: 'pointer',
              opacity: chat.status ? 1 : 0.5,
            }}
          >
            <p
              className="chat-user-id"
              style={{ fontWeight: 'bold', color: 'white' }}
            >
              {chat.name || chat.user_id}
            </p>
            <div style={{ color: 'lightgrey' }}>
              <MarkdownParser
                text={
                  chat.message_text.length > 50
                    ? `${chat.message_text.substring(0, 50)}...`
                    : chat.message_text
                }
              />
            </div>
            <span style={{ color: 'lightgrey', fontSize: '0.8rem' }}>
              {formatTimestamp(chat.timestamp)}
            </span>
          </li>
        ))}

        {hasMore && !loading && (
          <li
            ref={observerRef}
            style={{
              textAlign: 'center',
              padding: '1rem',
              color: 'grey',
            }}
          >
            Loading more...
          </li>
        )}
      </ul>

      {loading && <p style={{ margin: '1rem', textAlign: 'center' }}>Loading...</p>}
    </div>
  );
};

export default ChatList;
